import React, {
  useState,
  useCallback,
  type JSX,
  useRef,
  useEffect,
} from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Card,
  CardContent,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { assetsUrl } from "../../constants";

// ✅ Updated types: `name` instead of `title`
export type HelpNodeProps = {
  name?: string;
  text?: {
    heading?: string;
    paragraph?: string;
  };
  children?: HelpNodeProps[];
};

export type HelpNode = {
  children?: HelpNodeProps[];
};

type RecursiveAccordionProps = {
  node: HelpNodeProps;
  searchTerms: string[];
  level?: number;
  expandedNode?: string;
  imageUrl?: string;
  onAccordionChange?: (name: string) => void;
};

// 🔍 Highlight matched terms
const highlightText = (
  text: string,
  searchTerms: string[]
): (string | JSX.Element)[] => {
  if (!searchTerms.length || searchTerms.every((term) => !term.trim()))
    return [text];

  const regex = new RegExp(`(${searchTerms.filter(Boolean).join("|")})`, "gi");
  const parts = text.split(regex);

  return parts.map((part, i) =>
    regex.test(part) ? (
      <span key={i} style={{ backgroundColor: "transparent", fontWeight: 600 }}>
        {part}
      </span>
    ) : (
      part
    )
  );
};

export const RecursiveAccordion: React.FC<RecursiveAccordionProps> = ({
  node,
  searchTerms,
  level = 0,
  expandedNode,
  onAccordionChange,
}) => {
  const isLeaf = !node.children || node.children.length === 0;
  const isExpanded = expandedNode === node.name;
  const accordionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isExpanded && accordionRef.current) {
      accordionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isExpanded]);

  const handleChange = () => {
    if (onAccordionChange) {
      onAccordionChange(isExpanded ? "" : (node.name ?? ""));
    }
  };
  return (
    <div ref={accordionRef}>
      {" "}
      {/* ✅ Attach ref here */}
      <Accordion
        expanded={isExpanded}
        onChange={handleChange}
        sx={{
          ml: level * 2,
          boxShadow: "none",
          "&::before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {node.name && (
            <Typography>{highlightText(node.name, searchTerms)}</Typography>
          )}
        </AccordionSummary>

        <AccordionDetails>
          {isLeaf ? (
            <Card sx={{ boxShadow: "none", p: 0 }}>
              {/* Your content */}
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  py: 1,
                  px: 2,
                  transition: "all 0.2s ease",
                  "&:hover .text-box": {
                    transform: "translateX(14px)",
                  },
                  "&:hover .image-box": {
                    transform: "scale(1.05)",
                  },
                }}
              >
                {/* Image Section */}
                <Box
                  className="image-box"
                  sx={{
                    width: "70%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "transform 0.2s ease",
                  }}
                >
                  {imageUrl[node.name?.replace(/\s+/g, "").toLowerCase() as keyof typeof imageUrl] && (
                    <Box
                      component="img"
                      src={`${assetsUrl}${imageUrl[node.name?.replace(/\s+/g, "").toLowerCase() as keyof typeof imageUrl]}`}
                      alt={node.name}
                      sx={{
                        width: "100%",
                        height: "auto",
                        maxHeight: 400,
                        objectFit: "contain",
                        boxShadow: 1,
                      }}
                    />
                  )}
                </Box>

                {/* Text Section */}
                <Box
                  className="text-box"
                  sx={{
                    p: 3,
                    width: "50%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    transition: "transform 0.2s ease",
                  }}
                >
                  <Typography variant="h6">
                    {highlightText(
                      node.text?.heading ?? "Default Heading",
                      searchTerms
                    )}
                  </Typography>
                  <Typography variant="body2">
                    {highlightText(
                      node.text?.paragraph ?? "Default paragraph text",
                      searchTerms
                    )}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <NestedChildren
              childrenNodes={node.children ?? []}
              searchTerms={searchTerms}
              level={level + 1}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

const NestedChildren: React.FC<{
  childrenNodes: HelpNodeProps[];
  searchTerms: string[];
  level: number;
}> = ({ childrenNodes, searchTerms, level }) => {
  const [expandedName, setExpandedName] = useState<string>("");

  const handleAccordionChange = useCallback((name: string) => {
    setExpandedName(name);
  }, []);

  return (
    <>
      {childrenNodes.map((childNode, i) => (
        <RecursiveAccordion
          key={i}
          node={childNode}
          searchTerms={searchTerms}
          level={level}
          imageUrl={""}
          expandedNode={expandedName}
          onAccordionChange={handleAccordionChange}
        />
      ))}
    </>
  );
};
const imageUrl = {
  maindashboard: "/help/dashboard.png",
  reception: "/help/reception.png",
  lab: "/help/lab.png",
  radiology: "/help/radiology.png",
  patientbooking: "/help/PatientBooking.png",
  patientregistration: "/help/reception.png",
  emr: "/help/EMR.png",
  patientencounter: "/help/patientEncounter.png",
  vitals: "/help/vitals.png",
  procedure: "/help/procedure.png",
  billing: "/help/billing.png",
  cashier: "/help/cashier.png",
  claimlisting: "/help/claimlisting.png",
  priorapprovalstatus: "/help/priorapproval.png",
  remittancedashboard: "/help/remittanceDashboard.png",
  verification: "/help/verification.png",
  countrymaster: "/help/countryMaster.png",
  statemaster: "/help/stateMaster.png",
  appointmenttype: "/help/appointmentType.png",
  specialitymaster: "/help/specialityMaster.png",
  clinician: "/help/clinician.png",
  doctor: "/help/doctor.png",
  employee: "/help/employee.png",
  purpose: "/help/purpose.png",
  company: "/help/company.png",
  accounttag: "/help/accountTag.png",
  accountcategory: "/help/accountCategory.png",
  uom: "/help/uom.png",
  rosmaster: "/help/rosMaster.png",
  bloodgroupmaster: "/help/bloodGroupMaster.png",
  hpimaster: "/help/hpiMaster.png",
  complaintmaster: "/help/complaintMaster.png",
  icdmaster: "/help/icdMaster.png",
  diseasemaster: "/help/diseaseMaster.png",
  cptgroupmaster: "/help/cptGroupMaster.png",
  cptmaster: "/help/cptMaster.png",
  drugsmaster: "/help/drugsMaster.png",
  ratemaster: "/help/rateMaster.png",
  denialcodemaster: "/help/denialCodeMaster.png",
  loincmaster: "/help/loincMaster.png",
  frequencymaster: "/help/frequencyMaster.png",
  insurancetype: "/help/insuranceType.png",
  insurance: "/help/insurance.png",
  insuranceplan: "/help/insurancePlan.png",
  discountmaster: "/help/discountMaster.png",
  networkmaster: "/help/networkMaster.png",
  insurancepricemaster: "/help/insurancePriceMaster.png",
  pfshtemplate: "/help/pfshTemplate.png",
  mgntplanmaster: "/help/mgntPlanMaster.png",
  observationstemplatemaster: "/help/observationsTemplate.png",
  labtemplate: "/help/labTemplate.png",
  radiologytemplate: "/help/radiologyTemplate.png",
  packagemaster: "/help/packageMaster.png",
  clinicmanagement: "/help/clinicManagement.png",
  usergroup: "/help/userGroup.png",
  user: "/help/user.png",
  menu: "/help/menu.png",
  userprivilege: "/help/userPrivilege.png",
  loghistory: "/help/logHistory.png",
  userlog: "/help/userLog.png",
  expensemanager: "/help/expenseManager.png",
  postoffice: "/help/postOffice.png",
  insurancewiseoutstandingreport: "/help/insuranceOutstanding.png",
  "aging-report": "/help/aging.png",
  expensesreport: "/help/expenses.png",
  codificationstatusreport: "/help/codification.png",
  insuranceclaimsummary: "/help/claimSummary.png",
  insurancewiseclaim: "/help/insuranceClaim.png",
  claimdetailsreport: "/help/claimDetails.png",
  paymenttypewise: "/help/paymentType.png",
  billwisereport: "/help/billWise.png",
  departmentwise: "/help/departmentWise.png",
  userwise: "/help/userWise.png",
  patientwisereport: "/help/patientWise.png",
  doctorwisereport: "/help/doctorWise.png",
  cptgroupwise: "/help/cptGroupwise.png",
  encounterwise: "/help/encounterWise.png",
  dailyrevenuereport: "/help/dailyRevenue.png",
  dailyinvoicereport: "/help/dailyInvoice.png",
  monthlyinvoicereport: "/help/monthlyInvoice.png",
  encounterlistreport: "/help/encounterList.png",
  appointmentlistreport: "/help/appointmentList.png",
  patientlistreport: "/help/patientList.png",
};
